import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";

export default function CitiesIndex() {
  const { cities: CITIES, lang } = useLang();
  const [hover, setHover] = useState(null);

  return (
    <section
      data-testid={IDS.cities.grid}
      className="relative border-t border-border bg-background"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16 items-end">
          <div className="md:col-span-8">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-6">
              [ 03 · {lang === "fr" ? "Couverture" : "Coverage"} ]
            </div>
            <h2 className="font-serif font-light text-4xl md:text-7xl tracking-tighter leading-[0.95]">
              {CITIES.length} {lang === "fr" ? "Communautés." : "Communities."} <br />
              {lang === "fr" ? "Une " : "One "}<i className="text-primary">{lang === "fr" ? "province." : "Province."}</i>
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-foreground/70 leading-relaxed">
              {lang === "fr"
                ? "De la baie de Fundy à la Péninsule acadienne, nous opérons à l'échelle provinciale avec des agents locaux, une connaissance locale et un déploiement rapide."
                : "From the Bay of Fundy to the Acadian Peninsula, we operate province-wide with local officers, local knowledge and rapid deployment."}
            </p>
          </div>
        </div>

        <div
          className="relative border-t border-border"
          onMouseLeave={() => setHover(null)}
        >
          {CITIES.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{
                duration: 0.5,
                delay: i * 0.03,
                ease: [0.76, 0, 0.24, 1],
              }}
              className="border-b border-border"
              onMouseEnter={() => setHover(c.slug)}
            >
              <Link
                to={`/cities/${c.slug}`}
                data-testid={IDS.cities.row(c.slug)}
                className="group grid grid-cols-12 items-center py-6 md:py-8 px-2 md:px-4 hover:bg-[hsl(0_0%_6%)] transition-colors"
              >
                <div className="col-span-1 font-mono text-[10px] tracking-[0.25em] text-muted-foreground">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="col-span-6 md:col-span-5">
                  <div className="font-serif text-3xl md:text-5xl tracking-tight leading-none group-hover:text-primary transition-colors">
                    {c.name}
                  </div>
                </div>
                <div className="col-span-3 hidden md:block font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
                  {c.region}
                </div>
                <div className="col-span-5 md:col-span-2 text-right md:text-left font-mono text-[11px] text-muted-foreground">
                  {c.coords}
                </div>
                <div className="hidden md:flex col-span-1 justify-end">
                  <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-primary">
                    <span className="w-1.5 h-1.5 bg-primary" />
                    {lang === "fr" ? "Actif" : "Active"}
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}

          {/* Floating hover image */}
          <div className="hidden lg:block pointer-events-none absolute right-6 top-6 w-[320px] h-[420px] overflow-hidden border border-border">
            {CITIES.map((c) => (
              <div
                key={c.slug}
                className="absolute inset-0 transition-opacity duration-500"
                style={{ opacity: hover === c.slug ? 1 : 0 }}
              >
                <img
                  src={c.hero}
                  alt={c.name}
                  className="w-full h-full object-cover grayscale contrast-125"
                />
                <div className="absolute bottom-4 left-4 font-mono text-[10px] tracking-[0.25em] uppercase">
                  {c.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
